import { useQuery } from "@tanstack/react-query"

import {
  DETECTOR_SETTING_KEYS,
  SOURCE_LABEL,
  detectorProfileOptionsQuery,
  settingOf,
  valueLabel,
  type DetectorProfileOptions,
} from "@/components/domain/detector-profile"
import { SkeletonText } from "@/components/ui/alert"

import { type DatasetProfile, type DatasetProfileSetting } from "./dataset-profile"
import { type EvalRun } from "./eval-runs-data"

/**
 * The run's detector profile: what each setting resolved to, and — when the run used a derived
 * profile file — the evidence and the decision rule behind each value.
 *
 * Two runs are only comparable at the same settings, so this sits beside the figures rather than
 * in a tooltip.
 */
export function EvalRunProfile({ run }: { run: EvalRun }): JSX.Element {
  const options = useQuery(detectorProfileOptionsQuery())
  const resolved = run.detectorProfile
  const dataset = run.datasetProfile
  const recorded = resolved !== null || dataset !== null

  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-labelledby="eval-profile"
    >
      <h2 id="eval-profile" className="text-body font-semibold text-text">
        Detector profile
      </h2>
      {dataset && <DatasetLine dataset={dataset} />}

      {!recorded && (
        <p className="mt-2 text-body-sm text-text-3">
          This run did not record its detector settings.
        </p>
      )}
      {recorded && options.isPending && <SkeletonText lines={5} className="mt-3" />}
      {recorded && options.isError && (
        <p className="mt-2 text-body-sm text-text-3">The setting labels could not be loaded.</p>
      )}
      {recorded && options.isSuccess && (
        <ul className="mt-3 divide-y divide-border-soft">
          {DETECTOR_SETTING_KEYS.map((key) => {
            const derived = dataset?.settings.find((s) => s.key === key)
            const value = resolved?.values[key] ?? derived?.value ?? null
            const source = resolved?.source[key]
            return (
              <li key={key} className="py-2.5">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-body-sm text-text-2">
                    {settingOf(options.data, key)?.label ?? key}
                  </span>
                  <span className="text-body-sm font-medium text-text">
                    {value === null ? "Not recorded" : valueLabel(options.data, key, value)}
                  </span>
                  {source && (
                    <span className="text-caption text-text-3">{SOURCE_LABEL[source]}</span>
                  )}
                </div>
                {derived && <Evidence setting={derived} options={options.data} />}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function DatasetLine({ dataset }: { dataset: DatasetProfile }): JSX.Element {
  return (
    <p className="mt-1 text-caption text-text-3">
      Derived from {dataset.dataset ?? "a labelled dataset"}
      {dataset.codeRevision && (
        <>
          {" "}
          at revision <span className="font-mono">{dataset.codeRevision}</span>
        </>
      )}
      {dataset.derivedAt && <> on {dataset.derivedAt.slice(0, 10)}</>}.
    </p>
  )
}

function Evidence({
  setting,
  options,
}: {
  setting: DatasetProfileSetting
  options: DetectorProfileOptions
}): JSX.Element | null {
  if (setting.evidence.length === 0 && !setting.rule) return null
  return (
    <div className="mt-1.5 rounded-md bg-surface-sunken px-3 py-2">
      {setting.value && setting.value !== "auto" && (
        <p className="text-caption text-text-3">
          Derived value: {valueLabel(options, setting.key, setting.value)}
        </p>
      )}
      {setting.evidence.length > 0 && (
        <dl className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 font-mono text-caption">
          {setting.evidence.map((e) => (
            <div key={e.name} className="contents">
              <dt className="truncate text-text-2">{e.name}</dt>
              <dd className="text-right tabular-nums text-text">
                {typeof e.value === "number" ? e.value.toLocaleString() : e.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {setting.rule && <p className="mt-1.5 text-caption text-text-3">Rule: {setting.rule}</p>}
    </div>
  )
}
